import { NgIf } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  inject,
  viewChild,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { Subject, combineLatest, takeUntil } from 'rxjs';

import { AccountFacade } from 'ish-core/facades/account.facade';
import { CheckoutFacade } from 'ish-core/facades/checkout.facade';
import { PresidioAuthModalService } from 'ish-shared/components/login/auth-modals/presidio-auth-modal.service';
import {
  clearPresidioContextBannerChrome,
  schedulePresidioContextBannerChromeSync,
  syncPresidioContextBannerChrome,
} from 'src/app/extensions/presidio-demo/utils/presidio-context-banner-chrome';

import { SalesRepFacade } from '../../facades/sales-rep.facade';
import { SalesRepCustomerSwitchConfirmationModalComponent } from '../customer-switch-confirmation-modal/sales-rep-switch-confirmation-modal.component';

interface CustomerAccountOption {
  customerNo: string;
  companyName: string;
}

/**
 * MVP 8.2.1 — global banner while a sales rep is working in an affiliated customer account.
 * Mirrors CSR co-browse banner placement / sticky chrome sync; Switch opens Sales Accounts.
 */
@Component({
  selector: 'prs-sales-account-banner',
  templateUrl: './sales-rep-account-banner.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIf, TranslatePipe, SalesRepCustomerSwitchConfirmationModalComponent],
})
export class SalesRepAccountBannerComponent implements OnInit, AfterViewInit, OnDestroy {
  private accountFacade = inject(AccountFacade);
  private checkoutFacade = inject(CheckoutFacade);
  private salesRepFacade = inject(SalesRepFacade);
  private authModals = inject(PresidioAuthModalService);
  private cdr = inject(ChangeDetectorRef);
  private store = inject(Store);
  private host = inject(ElementRef<HTMLElement>);
  private readonly destroy$ = new Subject<void>();
  private resizeObserver?: ResizeObserver;

  readonly confirmationModal = viewChild<SalesRepCustomerSwitchConfirmationModalComponent>('confirmationModal');

  visible = false;
  accountLabel = '';
  busy = false;

  private accounts: CustomerAccountOption[] = [];
  private currentCustomerNo = '';
  private itemCount = 0;

  ngOnInit(): void {
    this.checkoutFacade.basketItemCount$.pipe(takeUntil(this.destroy$)).subscribe(count => {
      this.itemCount = count || 0;
    });

    combineLatest([this.accountFacade.user$, this.accountFacade.customer$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([user, customer]) => {
        this.accounts = (user?.availableSoldTos || []).map(account => ({
          customerNo: account.customerNo,
          companyName: account.companyName,
        }));
        this.currentCustomerNo = customer?.customerNo || '';
        const companyName =
          customer?.companyName ||
          this.accounts.find(account => account.customerNo === this.currentCustomerNo)?.companyName ||
          '';
        this.accountLabel = companyName || this.currentCustomerNo;
        // Single-affiliate users have no choice — hide Active account banner.
        this.visible = this.accounts.length > 1 && !!this.accountLabel;
        this.cdr.markForCheck();
        schedulePresidioContextBannerChromeSync(this.store);
      });
  }

  ngAfterViewInit(): void {
    if (!SSR && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.syncScrollChrome());
      const contextRow = document.querySelector('.prs-account-context-row');
      this.resizeObserver.observe(contextRow || this.host.nativeElement);
    }
    schedulePresidioContextBannerChromeSync(this.store);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.resizeObserver?.disconnect();
    this.clearScrollChrome();
  }

  async openSwitch(): Promise<void> {
    if (this.busy || !this.accounts.length) {
      return;
    }
    this.busy = true;
    this.cdr.markForCheck();
    try {
      const customerNo = await this.authModals.openSalesRepAccountSelect(this.accounts, {
        dismissible: true,
        currentCustomerNo: this.currentCustomerNo,
        titleKey: 'sales_rep.accounts.modal_title',
        hintKey: 'sales_rep.accounts.modal_hint',
        continueKey: 'sales_rep.accounts.modal_continue',
      });
      if (!customerNo || customerNo === this.currentCustomerNo) {
        return;
      }
      const companyName = this.accounts.find(account => account.customerNo === customerNo)?.companyName || customerNo;
      this.requestSwitch(customerNo, companyName);
    } finally {
      this.busy = false;
      this.cdr.markForCheck();
    }
  }

  onConfirmSwitch(customerNo: string): void {
    this.salesRepFacade.switchCustomer(customerNo);
  }

  onCancelSwitch(): void {
    // selection lives in the modal
  }

  private requestSwitch(customerNo: string, companyName: string): void {
    if (this.itemCount > 0) {
      const confirmationModal = this.confirmationModal();
      if (confirmationModal) {
        confirmationModal.customerID = customerNo;
        confirmationModal.customerName = companyName;
        confirmationModal.show();
        return;
      }
    }
    this.onConfirmSwitch(customerNo);
  }

  private syncScrollChrome(): void {
    syncPresidioContextBannerChrome(this.store);
  }

  private clearScrollChrome(): void {
    clearPresidioContextBannerChrome(this.store);
  }
}
